import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import * as api from "../constant/NewAPI";
import Utils from "../constant/Utils";
import { useCalendarApp, ScheduleXCalendar } from "@schedule-x/react";
import { createViewDay, createViewWeekAgenda, createViewMonthAgenda, createViewMonthGrid, createViewWeek, } from '@schedule-x/calendar';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { translations, mergeLocales } from '@schedule-x/translations';
import 'temporal-polyfill/global';
import '@schedule-x/theme-default/dist/index.css';
import { createEventModalPlugin } from '@schedule-x/event-modal'
import TrainingChart from "./TrainingChart";
import AssessmentChart from "./AssessmentChart";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";

const Dashboard = () => {

    const [summaryArray, setSummaryArray] = useState<any>(null);
    const [trainingArray, setTrainingArray] = useState<any[]>([]);
    const [assessmentArray, setAssessmentArray] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);


    const routes = all_routes;
    const userName = localStorage.getItem('FullName');
    const eventsService = useState(() => createEventsServicePlugin())[0];
    const calendarControls = createCalendarControlsPlugin();

    const today = Temporal.Now.plainDateISO()

    // Calculate 4 months before today
    const minDate = today.subtract({ months: 4 })
    const maxDate = today

    const mapSchedulesToEvents = (schedules: any[]) => {
        return schedules.flat().map((item: any, index: number) => {
            const startDate = new Date(`${item.scheduleDate} ${item.fromDate}`);
            const endDate = new Date(`${item.scheduleDate} ${item.toDate}`);
            return {
                // id: String(index + 1),
                id: `S-${index + 1}`, // keep unique IDs
                title: `${item.schedulesName} (${item.batchName})`,

                start: Temporal.ZonedDateTime.from({
                    year: startDate.getFullYear(),
                    month: startDate.getMonth() + 1,
                    day: startDate.getDate(),
                    hour: startDate.getHours(),
                    minute: startDate.getMinutes(),
                    timeZone: 'Asia/Kolkata',
                }),

                end: Temporal.ZonedDateTime.from({
                    year: endDate.getFullYear(),
                    month: endDate.getMonth() + 1,
                    day: endDate.getDate(),
                    hour: endDate.getHours(),
                    minute: endDate.getMinutes(),
                    timeZone: 'Asia/Kolkata',
                }),
                calendarId: 'training', // Assign to 'training' calendar
            }
        })
    }

    const mapAssessmentsToEvents = (assessments: any[]) => {
        return assessments.flat().map((item: any, index: number) => {
            const startDate = new Date(`${item.scheduleDate} ${item.fromDate}`);
            const endDate = new Date(`${item.scheduleDate} ${item.toDate}`);

            return {
                id: `A-${index + 1}`, // keep unique IDs
                title: `${item.schedulesName} (${item.batchName})`,

                start: Temporal.ZonedDateTime.from({
                    year: startDate.getFullYear(),
                    month: startDate.getMonth() + 1,
                    day: startDate.getDate(),
                    hour: startDate.getHours(),
                    minute: startDate.getMinutes(),
                    timeZone: 'Asia/Kolkata',
                }),

                end: Temporal.ZonedDateTime.from({
                    year: endDate.getFullYear(),
                    month: endDate.getMonth() + 1,
                    day: endDate.getDate(),
                    hour: endDate.getHours(),
                    minute: endDate.getMinutes(),
                    timeZone: 'Asia/Kolkata',
                }),
                calendarId: 'assessment', // Assign to 'assessment' calendar for different color

            };
        });
    };

    const customComponents = {
        eventModal: ({ calendarEvent }: any) => {

            const formatDate = (value: any) => {
                const d = value.toPlainDateTime?.() || value;
                const dd = String(d.day).padStart(2, "0");
                const mm = String(d.month).padStart(2, "0");
                const yy = String(d.year).slice(-2);
                return `${dd}-${mm}-${yy}`;
            };

            const formatTime = (value: any) => {
                const d = value.toPlainDateTime?.() || value;

                let hours = d.hour;
                const minutes = String(d.minute).padStart(2, "0");

                const ampm = hours >= 12 ? "PM" : "AM";
                hours = hours % 12;
                hours = hours ? hours : 12;

                return `${hours}:${minutes} ${ampm}`;
            };

            const getColor = (calendarId: string) => {
                switch (calendarId) {
                    case "training":
                        return "#1eb0d5"; // blue
                    case "assessment":
                        return "#0f2a4e"; // dark blue
                    default:
                        return "#9aa0a6"; // gray fallback
                }
            };
            const color = getColor(calendarEvent.calendarId);

            return (
                <div
                    style={{
                        padding: "24px",
                        background: "#fff",
                        borderRadius: "12px",
                        width: "300px",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        fontFamily: "Arial",
                        borderLeft: `6px solid ${color}`,
                    }}
                >
                    {/* Title */}
                    <h6 style={{ marginBottom: "10px", color: "#2b6cb0" }}>
                        {calendarEvent.title}
                    </h6>

                    {/* Date / Time */}
                    <p style={{ margin: "6px 0" }}>
                        <b>Date:</b>{formatDate(calendarEvent.start)}
                    </p>

                    <p style={{ margin: "6px 0" }}>
                        <b>Start Time:</b> {formatTime(calendarEvent.start)}
                    </p>

                    <p style={{ margin: "6px 0" }}>
                        <b>End Time:</b> {formatTime(calendarEvent.end)}
                    </p>

                </div>
            );
        },
    };


    const calendar = useCalendarApp({
        views: [createViewMonthGrid(), createViewDay(), createViewWeekAgenda(), createViewWeek(), createViewMonthAgenda()],
        events: [],
        plugins: [eventsService, createEventModalPlugin(), calendarControls],
        locale: 'en-IN',
        timezone: 'Asia/Kolkata',
        defaultView: 'month-grid',
        minDate,
        maxDate,
        monthGridOptions: {
            nEventsPerDay: 4, // Shows +N after 4 events
        },
       translations: mergeLocales(translations, {
        'enUS': {
            plusNEvents: '+ {n} events', // Single braces
        },
        'enIN': {
            plusNEvents: '+ {n} events',
        }
    }),
        callbacks: {
            onBeforeViewChange(nextDate) {
                if (Temporal.PlainDate.compare(nextDate, minDate) < 0) {
                    return minDate;
                }
                if (Temporal.PlainDate.compare(nextDate, maxDate) > 0) {
                    return maxDate;
                }
                return nextDate;
            }
        },
        calendars: {
            training: {
                colorName: 'training',
                lightColors: {
                    main: '#1eb0d5', // Main background color
                    container: '#bfe6f0',
                    onContainer: '#185c6d',
                },
            },
            assessment: {
                colorName: 'assessment',
                lightColors: {
                    main: '#88b1e8', // Different color
                    container: '#0f2a4e',
                    onContainer: '#ffffff',
                },
            },
        },
    })

    useEffect(() => {
        // eventsService.getAll()
        getSummary();
    }, [])

    function getSummary() {
        const summary = api.DashboardSummary;

        setIsLoading(true);
        setTimeout(() => {
            fetch(summary, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": 'Bearer ' + Utils.getAccessToken()
                }
            })
                .then((response) => response.json())
                .then((summaryRes) => {
                    if (summaryRes) {
                        // console.log('Summarytraining',summaryRes?.count?.scheduleCount);

                        setSummaryArray(summaryRes);
                        // Extract schedules from API
                        const schedules = summaryRes?.activityArray?.schedules || [];
                        // Convert to calendar events
                        const scheduleEvents = mapSchedulesToEvents(schedules);

                        const assessments = summaryRes?.activityArray?.assessments || [];
                        const assessmentEvents = mapAssessmentsToEvents(assessments);

                        setTrainingArray(summaryRes?.count?.scheduleCount?.flat() || [])
                        setAssessmentArray(summaryRes?.count?.assessmentCount?.flat() || [])

                        const allEvents = [...scheduleEvents, ...assessmentEvents];

                        //Push into calendar
                        eventsService.set(allEvents);
                        setIsLoading(false);

                    }
                })
                .catch((error) => {
                    console.log("Error fetching Institute List.", error)
                    setIsLoading(false);

                })
        }, 1000);
    }

    return (
        <>
            {isLoading && (
                <div id="global-loader">
                    <div className="loader-img">
                        <ImageWithBasePath src="assets/img/loader.png" className="img-fluid" alt="Global" />
                    </div>
                </div>
            )}
            {/* {!isLoading && ( */}
            <div className="content container">
                <div className="row">
                    <div className="sortby-section">
                        <div className="sorting-info">
                            <div className="d-lg-flex justify-content-between">
                                <h3 className=" mb-40">sAMSara Home</h3>
                                <h6 className=" mb-40">Welcome back, {userName}!</h6>
                            </div>
                            <div className="card dashboard-card statistics-card">
                                <div className="card-header">
                                    <h4>Quick Links</h4>
                                </div>
                                <div className="row">
                                    <div className="col-lg-6">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center text-uppercase">VIEW</h3>
                                                <ul className="statistics-list">
                                                    <li><Link to={routes.batches}><h6>Batches</h6></Link></li>
                                                    <li><Link to={routes.schedules}><h6>Schedules</h6></Link></li>
                                                    <li><Link to={routes.attendance}><h6>Attendance</h6></Link></li>
                                                    <li><Link to={routes.assessment}><h6>Assessment</h6></Link></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div><div className="col-lg-6">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center text-uppercase">Reports</h3>
                                                <ul className="statistics-list">
                                                    <li><Link to={routes.attendancereport}><h6>Attendance</h6></Link></li>
                                                    <li><Link to={routes.assessmentreport}><h6>Assessment</h6></Link></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card dashboard-card ">
                                <div className="card-header">
                                    <h4 className="text-uppercase">Four MONTH SUMMARY</h4>
                                </div>
                                <div className="card-body">
                                    <div className="col-lg-12">
                                        <div className="content-container" style={{ maxHeight: "600px", overflowY: "auto" }}>
                                            <ScheduleXCalendar calendarApp={calendar} customComponents={customComponents} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card dashboard-card statistics-card">
                                <div className="content container" style={{ maxWidth: "1380px", paddingTop: "10px" }}>
                                    <div className="row">
                                        <div className="row mb-10 justify-space-between">
                                            <div className="col-lg-6 mb-10">
                                                <div className="p-3 h-100" style={{ border: "0.5px solid #E7E8E9" }}>
                                                    <h6 className="text-center">Training Session Count</h6>
                                                    <TrainingChart allData={trainingArray} />
                                                </div>
                                            </div><div className="col-lg-6 mb-10">
                                                <div className="p-3 h-100" style={{ border: "0.5px solid #E7E8E9" }}>
                                                    <h6 className="text-center">Assessment Session Count</h6>
                                                    <AssessmentChart allData={assessmentArray} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card dashboard-card statistics-card">
                                <div className="row justify-content-center">
                                    <div className="col-lg-3">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center">No of Institutions</h3>
                                                <h5 className="text-center">{summaryArray?.count?.institutionCount}</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center">No of Batches</h3>
                                                <h5 className="text-center">{summaryArray?.count?.batchCount}</h5>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div className="col-lg-3">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center">No of Schedules</h3>
                                                <h5 className="text-center">{(summaryArray?.count?.scheduleCount)}</h5>
                                            </div>
                                        </div>
                                    </div> */}
                                    <div className="col-lg-3">
                                        <div className="statistics-grid flex-fill justify-content-center">
                                            <div className="statistics-content">
                                                <h3 className="text-center">No of Athletes</h3>
                                                <h5 className="text-center">{summaryArray?.count?.athleteCount}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* )} */}
        </>
    );
};

export default Dashboard;